import { FC } from "react";
import { Form, Grid } from "antd";
import { GUEST_DATA_MAP, GUEST_SECTIONS, GuestFieldContext } from "src/Modules/Hotels/constants/HotelBooking";
import { resolveComponent } from "./utils";
import classNames from "classnames";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import type { Guest } from "../../utils";

export const RenderField: FC<any> = ({ fieldName, fieldConfig, context }) => {
  const rules = resolveComponent(fieldConfig.rules, context);
  const initialValue = resolveComponent(fieldConfig.initialValue, context);
  const Component = resolveComponent(fieldConfig.component, context);
  if (Component === null) return null;
  return (
    <Form.Item
      name={[context.guestKey, ...(fieldConfig.parentPath ?? []), fieldName]}
      label={fieldConfig.label}
      layout={fieldConfig.layout}
      colon={false}
      initialValue={initialValue}
      rules={rules}
      valuePropName={fieldConfig.valuePropName}
      className={classNames("m-0! font-semibold", fieldConfig?.className)}
    >
      {Component}
    </Form.Item>
  );
};

function useGuestContext(guest: Guest, guests: Guest[], form: any): GuestFieldContext {
  const isLeadGuest = Form.useWatch([guest.key, "isLeadGuest"], { form, preserve: true });
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  return {
    guestType: guest.type,
    guestKey: guest.key,
    form,
    conditionFields: {
      isPrimaryGuest: !!isLeadGuest,
      otherGuests: guests.filter((otherGuest) => otherGuest.key !== guest.key),
      isLargeScreen,
    },
  };
}

export const GuestHeaderFields: FC<{ guest: Guest; guests: Guest[]; form: any }> = ({ guest, guests, form }) => {
  const allFields = GUEST_DATA_MAP[guest.type] || [];
  const headerFields = allFields.filter((col: any) => col.placement === "header");
  const context = useGuestContext(guest, guests, form);

  return (
    <>
      {headerFields.map((col: any) => (
        <RenderField key={col.name} fieldName={col.name} fieldConfig={col} context={context} />
      ))}
    </>
  );
};

const GuestFields: FC<{ guest: Guest; guests: Guest[]; form: any }> = ({ guest, guests, form }) => {
  const allFields = GUEST_DATA_MAP[guest.type] || [];
  const bodyFields = allFields.filter((col: any) => col.placement !== "header");
  const context = useGuestContext(guest, guests, form);

  return (
    <div className="@container flex flex-col gap-4">
      {Object.entries(GUEST_SECTIONS).map(([sectionKey, sectionValue]) => {
        const sectionFields = bodyFields
          .sort((a, b) => (a?.order ?? 0) - (b.order ?? 0))
          .filter((filters) => (sectionValue.inputTypes?.[guest.type] as string[])?.includes(filters?.name));

        if (sectionFields.length === 0) return null;

        return (
          <div
            key={sectionKey}
            className={classNames("@container bg-background-default flex flex-col gap-4 rounded-sm p-4 shadow-sm", sectionValue.className)}
          >
            <div>
              <div className="text-font-default text-lg font-semibold">{sectionValue.title(context.conditionFields)}</div>
              <div className="text-font-subtle text-sm font-semibold">{sectionValue.subtitle(context.conditionFields)}</div>
            </div>
            <div className="@sm:grid-cols-2 @md:grid-cols-2 grid grid-cols-1 gap-3">
              {sectionFields.map((col: any) => (
                <div key={col.name} className={col.fullWidth ? "col-span-full" : undefined}>
                  <RenderField fieldName={col.name} fieldConfig={col} context={context} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GuestFields;
